import React from 'react'
import papa from 'papaparse'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import {
  SortDirectionEnum,
  useStateWithDebounce,
  useEvents,
  useAuth,
  UserPermissions,
  useEventsLazy,
  SortByType,
  Events_events_events,
  getFormattedDateAndTime,
  EventStatus,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import { ROUTES } from 'internal-portal/constants/routes'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { EventDatalist } from 'sharedComponents/EventDatalist'
import { Box, Container } from 'components/Layout'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import Button from 'components/Button'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { Downloading } from 'components/Downloading'
import { format } from 'date-fns'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { CreateEventDialog } from './CreateEventDialog'
import { EVENT_STATUS_OPTIONS } from '../TestSessions/utils'

type EventsProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  id?: string
  name?: string
  companyOrGroup?: string
  status?: EventStatus
  dateFrom?: string
  dateTo?: string
}

const PAGE_LENGTH = 10

const EventsList: React.VFC<EventsProps> = () => {
  const { hasPermission } = useAuth()
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const history = useHistory()
  const searchState = useSearchState<FilterState>()
  const [isCreatingEvent, setIsCreatingEvent] = React.useState(false)
  const [eventIdEdit, setEventIdEdit] = React.useState<string>()
  const [downloadableDataUrl, setDownloadableDataUrl] = React.useState<string>()

  const { sorter, handleSortChange } = useSortChangeHandler()

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const [companyOrGroup, setCompanyOrGroup, debouncedCompanyOrGroup] = useStateWithDebounce(
    '',
    (companyOrGroup) => {
      searchState.setSearchState({ companyOrGroup, page: 1 }, false, true)
    }
  )

  const { data: eventsData, loading: isEventsLoading, refetch: refetchEvents } = useEvents({
    filter: {
      id: searchState.data.id,
      name: searchState.data.name,
      companyOrGroup: debouncedCompanyOrGroup,
      status: searchState.data.status,
      location: searchState.data.location,
      dateFrom: searchState.data.dateFrom,
      dateTo: searchState.data.dateTo,
    },
    page: Number(searchState.data.page) || 1,
    pageLength: PAGE_LENGTH,
    direction: sorter?.direction || SortDirectionEnum.DESC,
    sortBy: sorter?.sortBy === SortByType.DATE ? SortByType.EVENT_DAY : sorter?.sortBy,
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const [downloadEventsCsv, { data: eventsCsv, loading: isDownloadingEventsCsv }] = useEventsLazy({
    onCompleted: () => {
      const events = eventsCsv?.events?.events?.map((event: Events_events_events) => {
        const {
          id,
          EventDays,
          name,
          paymentMethod,
          testType,
          isActive,
          finishedAt,
          countAttendee,
          Company,
          Group,
          countSamples,
          sublocation,
        } = event

        const getStatus = () => {
          if (finishedAt) {
            return 'Finished'
          }
          if (isActive) {
            return 'Active'
          }
          return 'Inactive'
        }

        const events = {
          'Event ID': id,
          'Event Date': EventDays?.map((day) => format(new Date(day.startTime), 'MM/dd/yyyy')).join(
            ' | '
          ),
          'Event Name': name,
          'Payment Method': paymentMethod,
          'Rate type': sublocation,
          'Test Type': testType,
          'Number of people scheduled': countAttendee,
          'Number of people tested': countSamples,
          'Event Status': getStatus(),
          ...(Group && { 'Event Group': Group.name }),
          ...(Company && { 'Event Company': Company.name }),
        }
        return events
      })
      if (events && !downloadableDataUrl) {
        const csv = papa.unparse(events, { header: true })
        const dataBlob = new Blob([csv], { type: 'text/csv' })

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(dataBlob)
        link.setAttribute('download', `Events_${new Date().toISOString() || ''}.csv`)
        document.body.appendChild(link)
        link.click()
      } else if (downloadableDataUrl) {
        window.URL.revokeObjectURL(downloadableDataUrl)
        setDownloadableDataUrl('')
      }
    },
  })

  const handleDownloadCsv = () => {
    downloadEventsCsv({
      variables: {
        filterData: {
          id: searchState.data.id,
          name: searchState.data.name,
          companyOrGroup: debouncedCompanyOrGroup,
          status: searchState.data.status,
          location: searchState.data.location,
          dateFrom: searchState.data.dateFrom,
          dateTo: searchState.data.dateTo,
        },
        pagination: {
          from: 0,
          length: Number(eventsData?.events?.pagination.total),
        },
      },
    })
  }

  return (
    <DashboardPage
      Header={
        <>
          <Box display="flex" justifyContent="flex-end" p="1rem">
            {hasPermission([UserPermissions.SCHEDULE_EVENT]) && (
              <Button
                onClick={() => setIsCreatingEvent(true)}
                leftIcon={<Icon.Plus size="L" />}
                marginRight="0.25rem"
              >
                Schedule
              </Button>
            )}
            <Button appearance="filled" onClick={handleDownloadCsv} disabled={false}>
              {isDownloadingEventsCsv ? <Downloading /> : 'Download csv'}
            </Button>
          </Box>
        </>
      }
    >
      <Container>
        <EventDatalist
          events={eventsData?.events?.events || []}
          pagination={eventsData?.events?.pagination}
          isLoading={isEventsLoading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <TextInput
                name="id"
                label="Event ID"
                value={filterData.id || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by event ID"
                hasBottomContent={false}
              />
              <TextInput
                name="name"
                label="Event name"
                value={filterData.name || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by event name"
                hasBottomContent={false}
              />
              <TextInput
                name="groupOrCompany"
                label="Company/Group"
                value={companyOrGroup}
                onChange={(event) => setCompanyOrGroup(event.target.value)}
                leftIcon={<Icon.Search />}
                placeholder="Search by company or group"
                hasBottomContent={false}
              />
              <SelectInput
                name="status"
                label="Status"
                value={searchState.data.status || ''}
                onChange={handleInputChange}
                options={EVENT_STATUS_OPTIONS}
                hasBottomContent={false}
              />
              <TextInput
                name="location"
                label="Location"
                value={filterData.location || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by Location"
                hasBottomContent={false}
              />
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={searchState.data.dateFrom ? new Date(searchState.data.dateFrom) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent={false}
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={searchState.data.dateTo ? new Date(searchState.data.dateTo) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent={false}
                isFullWidth
              />
            </Box>
          }
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onRowClick={({ id }) => history.push(`/${ROUTES.TESTS}/${id}`)}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          openEditEvent={(eventId) => {
            setEventIdEdit(eventId)
            setIsCreatingEvent(true)
          }}
          pageLength={eventsData?.events?.pagination.length}
          refetchEvents={refetchEvents}
        />
      </Container>
      <CreateEventDialog
        isOpen={isCreatingEvent}
        onDismiss={() => {
          setIsCreatingEvent(false)
          setEventIdEdit('')
        }}
        refetchEvents={refetchEvents}
        eventIdEdit={eventIdEdit}
      />
    </DashboardPage>
  )
}

export default EventsList
